/**
 * HOF-696 D5/D6 — the one route wrapper for `/apps/labour`.
 *
 * Picks the chrome from the seat's permissions (never a role name):
 *  - a self-only labour seat (`labour.self.read`, no `farm.read`) → WorkerLayout
 *    with the worker app — and when the seat may also read the team (`labour.read`,
 *    a Vorarbeiter, HOF-697) its "Team" tab with the Timesheet; the app reads "my
 *    day" by the seat's `person_id`; on a farm without the app active it shows "Bitte im
 *    Büro melden" instead of redirecting — a worker's landing IS this route, so a
 *    redirect would loop;
 *  - a worker whose access ended (D12: no active seat, `labour_access_ended`) →
 *    WorkerLayout with "Bitte im Büro melden" — the landing sends them here, so
 *    this too must not redirect;
 *  - a seat that may open the office view (app active × `labour.read`) → the
 *    normal ProtectedLayout chrome with the Timesheet;
 *  - anyone else → /documents.
 *
 * It sits outside the ProtectedLayout route group, so it does its own auth check.
 */
import React from 'react';
import { Navigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import ProtectedLayout from '../../../layouts/ProtectedLayout';
import WorkerLayout from '../../../layouts/WorkerLayout';
import {
  DEFAULT_LANDING,
  canOpenFarmApp,
  hasSeatPermission,
  isLabourAccessEnded,
  isSelfOnlyLabourSeat,
} from '../../../utils/landing';
import WorkerApp from './WorkerApp';
import TimesheetPage from './TimesheetPage';
import LabourBlocked from './LabourBlocked';

export const LABOUR_APP_ID = 'labour';

const LabourRoute: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, userProfile } = useAuth();

  if (isLoading) {
    return (
      <div className="lab-loading" role="status">
        {t('labour.loading')}
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const farm = userProfile?.active_farm ?? null;

  if (isSelfOnlyLabourSeat(farm)) {
    const appActive = Boolean(farm?.apps?.includes(LABOUR_APP_ID));
    return (
      <WorkerLayout>
        {appActive ? (
          <WorkerApp
            draftScope={`${farm?.tenant_id ?? ''}:${userProfile?.user_id ?? ''}`}
            personId={farm?.person_id ?? null}
            teamView={hasSeatPermission(farm, 'labour.read') ? <TimesheetPage /> : undefined}
          />
        ) : (
          <LabourBlocked reason="app_not_active" />
        )}
      </WorkerLayout>
    );
  }

  if (isLabourAccessEnded(userProfile)) {
    return (
      <WorkerLayout>
        <LabourBlocked reason="access_ended" />
      </WorkerLayout>
    );
  }

  if (canOpenFarmApp(farm, LABOUR_APP_ID, ['labour.read'])) {
    return (
      <ProtectedLayout>
        <TimesheetPage />
      </ProtectedLayout>
    );
  }

  return <Navigate to={DEFAULT_LANDING} replace />;
};

export default LabourRoute;
